import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {SelectionTargetTypeEnum} from "../../enum/selection-target-type.enum";
import {
    AvailableFieldsResponse,
    FieldCondition,
    RuleGroupNode,
    SaveSelectionCriteriaRequest,
    SelectionCriteriaTree,
    SelectionPreviewRequest,
    SelectionPreviewResponse,
    ValidationResult
} from "../../model/dto/api/selection-criteria-tree.interface";
import {RealTimeCountRequest, RealTimeCountResponse} from "../../model/dto/api/real-time-count.interface";

@Injectable({
    providedIn: 'root'
})
export class SelectionCriteriaService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/selectionCriterias')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-filter';
    }

    /**
     * Gets the complete selection criteria tree for a campaign.
     */
    getCriteriaTree(campaignId: number): Observable<SelectionCriteriaTree> {
        return this.getGeneric<SelectionCriteriaTree>(`/campaign/${campaignId}/tree`);
    }

    /**
     * Gets or creates selection criteria for a campaign.
     */
    getOrCreateCriteriaTree(campaignId: number): Observable<SelectionCriteriaTree> {
        return this.postGeneric<SelectionCriteriaTree>({}, `/campaign/${campaignId}/tree`);
    }

    /**
     * Saves the complete selection criteria tree.
     */
    saveCriteriaTree(request: SaveSelectionCriteriaRequest): Observable<SelectionCriteriaTree> {
        return this.http.put<SelectionCriteriaTree>(`${this.apiUrl}/tree`, request);
    }

    /**
     * Adds a new rule group to the criteria tree.
     */
    addRuleGroup(criteriaId: number, groupDto: Partial<RuleGroupNode>, parentGroupId?: number): Observable<RuleGroupNode> {
        const url = parentGroupId
            ? `/${criteriaId}/groups?parentGroupId=${parentGroupId}`
            : `/${criteriaId}/groups`;
        return this.postGeneric<RuleGroupNode>(groupDto, url);
    }

    /**
     * Updates an existing rule group.
     */
    updateRuleGroup(groupId: number, groupDto: Partial<RuleGroupNode>): Observable<RuleGroupNode> {
        return this.http.put<RuleGroupNode>(`${this.apiUrl}/groups/${groupId}`, groupDto);
    }

    /**
     * Deletes a rule group and all its children.
     */
    deleteRuleGroup(groupId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/groups/${groupId}`);
    }

    /**
     * Adds a field condition to a rule group.
     */
    addFieldCondition(groupId: number, conditionDto: Partial<FieldCondition>): Observable<FieldCondition> {
        return this.postGeneric<FieldCondition>(conditionDto, `/groups/${groupId}/conditions`);
    }

    /**
     * Updates an existing field condition.
     */
    updateFieldCondition(conditionId: number, conditionDto: Partial<FieldCondition>): Observable<FieldCondition> {
        return this.http.put<FieldCondition>(`${this.apiUrl}/conditions/${conditionId}`, conditionDto);
    }

    /**
     * Deletes a field condition.
     */
    deleteFieldCondition(conditionId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/conditions/${conditionId}`);
    }

    /**
     * Executes a preview of the selection criteria and returns results.
     */
    executePreview(criteriaId: number, request?: SelectionPreviewRequest): Observable<SelectionPreviewResponse> {
        return this.postGeneric<SelectionPreviewResponse>(request || {}, `/${criteriaId}/preview`);
    }

    /**
     * Validates the selection criteria.
     */
    validateCriteria(criteriaId: number): Observable<ValidationResult> {
        return this.getGeneric<ValidationResult>(`/${criteriaId}/validate`);
    }

    /**
     * Gets available fields for selection based on target type.
     */
    getAvailableFields(targetType: SelectionTargetTypeEnum): Observable<AvailableFieldsResponse> {
        const targetTypeValue = SelectionTargetTypeEnum[targetType].toUpperCase();
        return this.getGeneric<AvailableFieldsResponse>(`/fields?targetType=${targetTypeValue}`);
    }

    /**
     * Estimates member count in real-time for the selection criteria.
     * Returns live count with delta indicator as criteria are being built.
     * Implements F-025: Real-Time Member Count Updates.
     */
    estimateRealTimeCount(criteriaId: number, request?: RealTimeCountRequest): Observable<RealTimeCountResponse> {
        return this.postGeneric<RealTimeCountResponse>(request || {}, `/${criteriaId}/realTimeCount`);
    }

    /**
     * Gets the latest real-time count estimate for the selection criteria.
     */
    getLatestRealTimeCount(criteriaId: number): Observable<RealTimeCountResponse> {
        return this.getGeneric<RealTimeCountResponse>(`/${criteriaId}/realTimeCount/latest`);
    }
}
